import "@/App.css";
import { useEffect, useRef } from "react";
import {
  BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate,
} from "react-router-dom";
import { Toaster } from "sonner";
import { api } from "@/lib/api";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Friends from "@/pages/Friends";
import Places from "@/pages/Places";
import Rewards from "@/pages/Rewards";
import AdminSchools from "@/pages/AdminSchools";
import AdminAnalytics from "@/pages/AdminAnalytics";
import WalkWithFriend from "@/pages/WalkWithFriend";
import WalkWatch from "@/pages/WalkWatch";
import PublicAvailability from "@/pages/PublicAvailability";
import InviteAccept from "@/pages/InviteAccept";
import ProtoDark from "@/pages/ProtoDark";

function AuthCallback() {
  const navigate = useNavigate();
  const { setUser, checkAuth } = useAuth();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = window.location.hash;
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        const res = await api.post("/auth/session", { session_id: sid });
        const chosen = localStorage.getItem("routerev_school");
        // Skip the picker choice when the .edu domain auto-matched a live campus.
        if (chosen && !res?.data?.campus_matched) {
          try { await api.put("/me/school", { school_id: chosen }); } catch {}
        }
        await checkAuth();
        const pendingInvite = localStorage.getItem("routerev_invite");
        if (pendingInvite) {
          localStorage.removeItem("routerev_invite");
          window.history.replaceState(null, "", "/");
          navigate(`/invite/${pendingInvite}`, { replace: true });
          return;
        }
        window.history.replaceState(null, "", "/");
        navigate("/", { replace: true });
      } catch (err) {
        if (err?.response?.status === 403 && err?.response?.data?.detail?.code === "not_edu") {
          try { localStorage.setItem("jeeo_edu_block", err.response.data.detail.email || "1"); } catch {}
        }
        window.history.replaceState(null, "", "/");
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, setUser, checkAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F5F4]">
      <div className="mono text-sm tracking-[0.2em] uppercase text-brand animate-pulse">
        Signing you in…
      </div>
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="mono text-xs tracking-[0.2em] uppercase text-brand animate-pulse">
          Loading campus…
        </div>
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/proto/dark" element={<ProtoDark />} />
      <Route path="/s/:shareId" element={<PublicAvailability />} />
      <Route path="/walk/w/:token" element={<WalkWatch />} />
      <Route path="/" element={<Protected><Dashboard /></Protected>} />
      <Route path="/places" element={<Protected><Places /></Protected>} />
      <Route path="/rewards" element={<Protected><Rewards /></Protected>} />
      <Route path="/ideas" element={<Navigate to="/rewards" replace />} />
      <Route path="/friends" element={<Protected><Friends /></Protected>} />
      <Route path="/invite/:code" element={<InviteAccept />} />
      <Route path="/walk" element={<Protected><WalkWithFriend /></Protected>} />
      <Route path="/admin/schools" element={<Protected><AdminSchools /></Protected>} />
      <Route path="/admin/analytics" element={<Protected><AdminAnalytics /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-center" richColors />
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
